import { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { buildRoomPackageOptionsViewOrder } from 'lib/offer/offerPageUtil'
import type { OfferPageRoom, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'
import { EmptyArray } from 'lib/array/arrayUtils'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import Pane from 'components/Common/Pane'
import BodyText from 'LuxKit/Typography/BodyText'
import { useIntl } from 'react-intl'
import FlashMobileStickyHeaderFilters from '../FlashMobileStickyHeaderFilters'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Sheet from 'components/utils/Sheet'
import FlashHotelRateOption from 'components/OfferPage/FlashHotel/SameRoom/FlashHotelRateOption'
import AccommodationRoomTileMessage from 'components/AccommodationKit/AccommodationRoomTileMessage'
import {
  useAirportTransferPackageOptions,
  useAirportTransferPackageSelection,
} from 'components/OfferPage/FlashHotel/SameRoom/useAirportTransferPackageOptions'
import { getBasePackageUniqueKey } from 'lib/offer/offerUtils'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const CarouselContainer = styled.div`
  padding: ${rem(12)};
`

const CarouselItemContainer = styled(Sheet)`
  min-width: 75vw;
  width: 100%;
`

interface Props {
  room: App.PackageRoomType
  rooms: Array<OfferPageRoom>
  packageOptions: Array<App.OfferPackageOptionView>
  selectedPackageKey?: string
  onClose?: () => void
  onBack?: () => void
}

function FlashHotelPackageSelectionModalWithCarousel(props: Props) {
  const {
    room,
    rooms,
    packageOptions,
    selectedPackageKey,
    onClose,
    onBack,
  } = props

  const intl = useIntl()
  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrder(packageOptions.filter(option => option.capacityValid))
  }, [packageOptions])

  const packageViews = useMemo(() => packageOptionsByRoom.find(option => option.roomId === room?.id)?.packageViews ?? [], [packageOptionsByRoom, room?.id])
  const packagesToRender = useAirportTransferPackageOptions(packageViews)
  const hasExceededCapacity = packageViews.length === 0 && packageOptions.some(option =>
    option.package.roomType?.id === room?.id && !option.capacityValid,
  )
  const {
    getPackageSelection,
    onAirportTransferToggle,
  } = useAirportTransferPackageSelection(rooms, selectedPackageKey)

  const modalTitle = intl.formatMessage({
    id: 'qqlOD-O8y4W_Kyp8kGyb',
    defaultMessage: 'Select a package',
    description: 'Heading title for package selection modal on the offer detail page',
  })

  const availablePackagesText = intl.formatMessage(
    {
      id: 'bkbbhVqu1OAc0_Dd3vET',
      defaultMessage: '{count, plural, one {# package option} other {# package options}} available',
      description: 'Text showing number of package options available on the offer detail page',
    },
    {
      count: packagesToRender.length,
    },
  )

  return (
    <>
      <ModalHeader
        title={modalTitle}
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <FlashMobileStickyHeaderFilters />
      </ModalHeader>
      <ModalBody>
        <Pane background="none">
          {packagesToRender.length > 1 && (
            <OptionsContainer>
              <BodyText variant="small" colour="light-foreground-alt">{availablePackagesText}</BodyText>
            </OptionsContainer>
          )}

          <CarouselContainer>
            {hasExceededCapacity && <AccommodationRoomTileMessage messageType="exceeded-capacity" />}
            {packagesToRender.length > 0 && <CardCarousel
              snap="start"
              width={packagesToRender.length === 1 ? '100%' : 'auto'}
              gap={12}
            >
              {packagesToRender.map((view, index) => {
                const airportTransferPackageOption = view.airportTransferPackageOption
                const {
                  airportTransferBasePackageKey,
                  airportTransferSelected,
                  selectedPackageView,
                } = getPackageSelection(view)

                return (
                  <CarouselItemContainer
                    key={getBasePackageUniqueKey(view.package)}
                    border="light-foreground"
                  >
                    <FlashHotelRateOption
                      packageView={selectedPackageView}
                      displayPackageView={airportTransferPackageOption?.basePackageView}
                      packagePosition={packagesToRender.length > 1 ? index + 1 : undefined}
                      airportTransferPriceDifference={airportTransferPackageOption?.priceDifference}
                      airportTransferInclusion={airportTransferPackageOption?.transferInclusion}
                      airportTransferBasePackageKey={airportTransferBasePackageKey}
                      airportTransferSelected={airportTransferSelected}
                      onAirportTransferToggle={onAirportTransferToggle}
                    />
                  </CarouselItemContainer>
                )
              })}
            </CardCarousel>}
          </CarouselContainer>
        </Pane>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  room: state.room,
  rooms: state.rooms ?? EmptyArray,
  selectedPackageKey: state.selectedPackageKey,
  packageOptions: getDurationPackageOptions(state, appState),
}), FlashHotelPackageSelectionModalWithCarousel)
